import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { submitMessageBooking, formatApiError } from "@/lib/api";
import { EASE } from "@/lib/motion";

const inputCls =
  "w-full border border-steel-border bg-steel-elevated px-4 py-3 text-sm text-steel-text placeholder:text-steel-muted transition-colors duration-300 focus:border-steel-accent focus:outline-none rounded-none";

export default function MessageForm({ heading, subheading }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return toast.error("Add a short message so Jared knows how to help.");
    setStatus("sending");
    try {
      await submitMessageBooking({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      setStatus("done");
      toast.success("Message sent — Jared will be in touch.");
    } catch (err) {
      setStatus("idle");
      toast.error(formatApiError(err));
    }
  };

  if (status === "done") {
    return (
      <motion.div
        data-testid="message-success"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="border border-steel-brass/40 bg-steel-surface p-10"
      >
        <div className="flex h-12 w-12 items-center justify-center border border-steel-brass/50">
          <Check className="h-5 w-5 text-steel-brass" />
        </div>
        <h3 className="mt-6 font-display text-2xl font-medium text-steel-text">Message sent, {form.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-sm leading-relaxed text-steel-secondary">
          Jared reads every message personally and typically replies within one business day. Keep an eye on {form.email}.
        </p>
        <button
          data-testid="message-reset-btn"
          onClick={() => {
            setForm({ name: "", email: "", phone: "", message: "" });
            setStatus("idle");
          }}
          className="mt-8 text-sm font-medium text-steel-brass transition-colors hover:text-steel-text"
        >
          Send another message
        </button>
      </motion.div>
    );
  }

  return (
    <div>
      {(heading || subheading) && (
        <div className="mb-8">
          {heading && <h3 className="font-display text-2xl font-medium text-steel-text">{heading}</h3>}
          {subheading && <p className="mt-2 text-sm text-steel-secondary">{subheading}</p>}
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-5" data-testid="message-form">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">Full name *</label>
            <input
              data-testid="message-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
              placeholder="Jane Citizen"
            />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">Email *</label>
            <input
              data-testid="message-email-input"
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
              placeholder="jane@example.com"
            />
          </div>
        </div>
        <div>
          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">Phone (optional)</label>
          <input
            data-testid="message-phone-input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className={inputCls}
            placeholder="(555) 000-0000"
          />
        </div>
        <div>
          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">Your message *</label>
          <textarea
            data-testid="message-body-input"
            rows={5}
            required
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className={`${inputCls} resize-none`}
            placeholder="Tell Jared what's going on. Please don't include Social Security numbers or account numbers here — those move through the secure portal later."
          />
        </div>
        <button
          data-testid="message-submit-button"
          type="submit"
          disabled={status === "sending"}
          className="group flex w-full items-center justify-center gap-2 bg-steel-text px-8 py-4 text-sm font-semibold tracking-wide text-steel-bg transition-colors duration-300 hover:bg-zinc-300 disabled:opacity-60 sm:w-auto"
        >
          {status === "sending" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Sending…
            </>
          ) : (
            <>
              Send Message
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </>
          )}
        </button>
      </form>
    </div>
  );
}
